<template>
    <div class="scanner-container">
        <div v-if="error" class="scanner-error pa-3">{{ error }}</div>
        <div v-show="!isLoading && !error" class="video-wrap" @click="focusTap">
            <video poster="data:image/gif,AAAA" ref="scanner" playsinline muted></video>
            <div class="overlay-element" :style="{ clipPath: clip, webkitClipPath: clip }"></div>
            <div class="scan-box" :style="boxStyle">
                <div class="laser"></div>
            </div>
            <v-btn v-if="zoomSteps.length > 1" class="zoom" small dark color="secondary" @click.stop="cycleZoom">
                {{ zoomLevel }}x
            </v-btn>
            <v-btn v-if="torchSupported" class="torch" small fab dark :color="torchOn ? 'warning' : 'secondary'" @click.stop="toggleTorch">
                <v-icon dark>{{ torchOn ? 'mdi-flashlight-off' : 'mdi-flashlight' }}</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
import { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat, BinaryBitmap, HybridBinarizer } from "@zxing/library";
import { HTMLCanvasElementLuminanceSource } from "@zxing/library/esm/browser/HTMLCanvasElementLuminanceSource";

// Scanbereich in Anteilen des sichtbaren Videobilds. Dieser Ausschnitt wird markiert UND ausgewertet,
// d.h. nur ein Code innerhalb des Rahmens wird erkannt (wichtig, wenn mehrere Flaschen im Bild sind).
const SCAN = { x0: 0.1, x1: 0.9, y0: 0.2, y1: 0.8 };
const pct = v => (v * 100) + "%";

export default {
    name: "barcode-scanner",

    data() {
        return {
            isLoading: true,
            error: "",
            torchSupported: false,
            torchOn: false,
            zoomSteps: [],
            zoomLevel: 1,
            codeReader: null,
        };
    },

    computed: {
        clip() {
            const { x0, x1, y0, y1 } = SCAN;
            const p = [[0, 0], [0, 1], [x0, 1], [x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, 1], [1, 1], [1, 0]];
            return "polygon(" + p.map(([x, y]) => pct(x) + " " + pct(y)).join(",") + ")";
        },
        boxStyle() {
            return { left: pct(SCAN.x0), top: pct(SCAN.y0), width: pct(SCAN.x1 - SCAN.x0), height: pct(SCAN.y1 - SCAN.y0) };
        },
    },

    mounted() {
        if (!(navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
            this.error = "Kamera wird von diesem Gerät/Browser nicht unterstützt.";
            return;
        }
        document.addEventListener("visibilitychange", this.onVisibility);
        this.start();
    },

    beforeDestroy() {
        document.removeEventListener("visibilitychange", this.onVisibility);
        this.stop();
    },

    methods: {
        onVisibility() {
            if (document.visibilityState === "hidden") {
                this.stop();
            } else if (!this.codeReader) {
                this.start();
            }
        },

        stop() {
            if (this.codeReader) {
                this.codeReader.reset();
                this.codeReader = null;
            }
            this.isLoading = true;
            this.torchOn = false;
            this.torchSupported = false;
            this.zoomSteps = [];
        },

        async pickDeviceId() {
            // Gewählte Kamera merken: spart das zusätzliche Öffnen/Schließen der Kamera bei jedem Start
            try {
                const cached = localStorage.getItem("camera-device");
                if (cached) return cached;
            } catch (e) { /* ignorieren */ }
            // Erst Berechtigung holen, damit enumerateDevices Labels liefert
            const tmp = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
            tmp.getTracks().forEach(t => t.stop());
            const devices = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "videoinput");
            const back = devices.filter(d => /back|rear|environment|rück/i.test(d.label));
            const pool = back.length ? back : devices;
            // Ultraweitwinkel/Tele meiden, haben oft keinen Nahfokus
            const main = pool.find(d => !/wide|ultra|tele|depth|macro/i.test(d.label)) || pool[0];
            if (main) {
                try { localStorage.setItem("camera-device", main.deviceId); } catch (e) { /* ignorieren */ }
            }
            return main ? main.deviceId : undefined;
        },

        // Liefert eine Funktion canvas -> Text|null. Eingebauter Detektor (Android/Chrome) bevorzugt, sonst zxing.
        async createDecoder() {
            if ("BarcodeDetector" in window) {
                try {
                    const wanted = ["code_128", "code_39", "ean_13", "ean_8", "itf", "qr_code", "data_matrix", "codabar", "upc_a"];
                    const supported = await window.BarcodeDetector.getSupportedFormats();
                    const formats = wanted.filter(f => supported.includes(f));
                    if (formats.length) {
                        const detector = new window.BarcodeDetector({ formats });
                        const fn = async canvas => {
                            const codes = await detector.detect(canvas);
                            return codes.length ? codes[0].rawValue : null;
                        };
                        fn.interval = 100;
                        return fn;
                    }
                } catch (e) { /* weiter mit zxing */ }
            }
            const hints = new Map();
            hints.set(DecodeHintType.TRY_HARDER, true);
            hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.EAN_13,
                BarcodeFormat.EAN_8, BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
                BarcodeFormat.DATA_MATRIX, BarcodeFormat.CODABAR, BarcodeFormat.UPC_A,
            ]);
            const reader = new BrowserMultiFormatReader(hints);
            const fn = async canvas => {
                try {
                    const bitmap = new BinaryBitmap(new HybridBinarizer(new HTMLCanvasElementLuminanceSource(canvas)));
                    return reader.decodeBitmap(bitmap).getText();
                } catch (e) {
                    return null; // nichts gefunden
                }
            };
            fn.interval = 120;
            return fn;
        },

        // Kopiert den markierten Scanbereich (in voller Kameraauflösung) auf das Canvas.
        // Berücksichtigt, dass das Video per object-fit: cover beschnitten dargestellt wird.
        drawRegion(video, canvas) {
            const vw = video.videoWidth, vh = video.videoHeight;
            const cw = video.clientWidth, ch = video.clientHeight;
            if (!vw || !vh || !cw || !ch) return false;
            const scale = Math.max(cw / vw, ch / vh);
            const visW = cw / scale, visH = ch / scale;
            const offX = (vw - visW) / 2, offY = (vh - visH) / 2;
            const sx = offX + visW * SCAN.x0, sy = offY + visH * SCAN.y0;
            const sw = visW * (SCAN.x1 - SCAN.x0), sh = visH * (SCAN.y1 - SCAN.y0);
            const k = Math.min(1, 1280 / sw);
            canvas.width = Math.round(sw * k);
            canvas.height = Math.round(sh * k);
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            return true;
        },

        async start() {
            this.error = "";
            this.isLoading = true;
            const handle = { reset() { /* wird gesetzt, sobald die Kamera läuft */ } };
            this.codeReader = handle;
            try {
                const deviceId = await this.pickDeviceId();
                if (this.codeReader !== handle) return; // inzwischen gestoppt

                const constraints = {
                    video: {
                        deviceId: deviceId ? { exact: deviceId } : undefined,
                        facingMode: deviceId ? undefined : "environment",
                        width: { ideal: 1920 },
                        height: { ideal: 1080 },
                    },
                };
                const decoder = await this.createDecoder();
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                let running = true;
                let timer = null;
                handle.reset = () => {
                    running = false;
                    clearTimeout(timer);
                    stream.getTracks().forEach(t => t.stop());
                    const v = this.$refs.scanner;
                    if (v) v.srcObject = null;
                };
                if (this.codeReader !== handle) { handle.reset(); return; }

                const video = this.$refs.scanner;
                video.oncanplay = () => {
                    this.isLoading = false;
                    this.$emit("loaded");
                    this.setupTrack(video);
                };
                video.srcObject = stream;
                await video.play().catch(() => {});

                const canvas = document.createElement("canvas");
                const loop = async () => {
                    if (!running) return;
                    try {
                        if (video.readyState >= 2 && this.drawRegion(video, canvas)) {
                            const text = await decoder(canvas);
                            if (running && text) this.$emit("decode", text);
                        }
                    } catch (e) { /* einzelnes Bild nicht auswertbar */ }
                    if (running) timer = setTimeout(loop, decoder.interval);
                };
                loop();
            } catch (e) {
                if (this.codeReader === handle) this.codeReader = null;
                if (e && e.name === "OverconstrainedError" && !this.retried) {
                    // gemerkte Kamera existiert nicht mehr -> neu suchen
                    this.retried = true;
                    try { localStorage.removeItem("camera-device"); } catch (err) { /* ignorieren */ }
                    return this.start();
                }
                this.error = this.errorText(e);
                this.$emit("error", e);
            }
        },

        getTrack() {
            const video = this.$refs.scanner;
            const stream = video && video.srcObject;
            return stream && stream.getVideoTracks && stream.getVideoTracks()[0];
        },

        setupTrack() {
            const track = this.getTrack();
            if (!track || !track.getCapabilities) return;
            const caps = track.getCapabilities();
            if (caps.focusMode && caps.focusMode.includes("continuous")) {
                track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
            }
            // Zoom: ein größeres Bild hilft, wenn die Kamera aus nächster Nähe nicht scharf stellt
            if (caps.zoom) {
                const steps = [1, 2, 3].filter(z => z >= caps.zoom.min && z <= caps.zoom.max);
                this.zoomSteps = steps;
                let last = 1;
                try { last = Number(localStorage.getItem("zoom-last")) || 1; } catch (e) { /* ignorieren */ }
                this.zoomLevel = steps.includes(last) ? last : steps[0];
                if (this.zoomLevel !== 1) this.applyZoom(track);
            }
            this.torchSupported = !!caps.torch;
            if (this.torchSupported && this.initialTorch()) this.setTorch(track, true);
        },

        applyZoom(track) {
            track.applyConstraints({ advanced: [{ zoom: this.zoomLevel }] }).catch(() => {});
            try { localStorage.setItem("zoom-last", String(this.zoomLevel)); } catch (e) { /* ignorieren */ }
        },

        cycleZoom() {
            const track = this.getTrack();
            if (!track || !this.zoomSteps.length) return;
            const i = this.zoomSteps.indexOf(this.zoomLevel);
            this.zoomLevel = this.zoomSteps[(i + 1) % this.zoomSteps.length];
            this.applyZoom(track);
        },

        // Antippen des Bilds: einmal neu scharf stellen, danach wieder Dauerfokus
        focusTap() {
            const track = this.getTrack();
            if (!track || !track.getCapabilities) return;
            const modes = track.getCapabilities().focusMode || [];
            if (!modes.includes("single-shot")) return;
            track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] })
                .then(() => setTimeout(() => {
                    if (modes.includes("continuous")) track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
                }, 1500))
                .catch(() => {});
        },

        initialTorch() {
            try {
                const s = JSON.parse(localStorage.getItem("settings") || "{}");
                const mode = s.taschenlampe || "last";
                if (mode === "on") return true;
                if (mode === "last") return localStorage.getItem("torch-last") === "1";
            } catch (e) { /* localStorage nicht verfügbar */ }
            return false;
        },

        setTorch(track, on) {
            return track.applyConstraints({ advanced: [{ torch: on }] })
                .then(() => {
                    this.torchOn = on;
                    try { localStorage.setItem("torch-last", on ? "1" : "0"); } catch (e) { /* ignorieren */ }
                })
                .catch(() => {});
        },

        toggleTorch() {
            const track = this.getTrack();
            if (!track) return;
            this.setTorch(track, !this.torchOn);
        },

        errorText(e) {
            switch (e && e.name) {
                case "NotAllowedError":
                case "SecurityError":
                    return "Kamerazugriff verweigert. Bitte in den Browser-/Geräteeinstellungen die Kamera für diese App erlauben.";
                case "NotFoundError":
                case "OverconstrainedError":
                    return "Keine geeignete Kamera gefunden.";
                case "NotReadableError":
                    return "Kamera wird von einer anderen App verwendet oder konnte nicht gestartet werden.";
                default:
                    return "Kamera konnte nicht gestartet werden.";
            }
        },
    },
};
</script>

<style scoped>
video {
    display: block;
    width: 100%;
    height: 100%;
    /* Bild füllt den begrenzten Bereich, damit es nicht aus dem Browserfenster ragt */
    object-fit: cover;
}
.video-wrap {
    position: relative;
    overflow: hidden;
    height: 40vh;
    max-height: 320px;
}
.scanner-container {
    position: relative;
}
.scanner-error {
    color: #F44336;
}
.torch {
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 3;
}
.zoom {
    position: absolute;
    left: 8px;
    bottom: 8px;
    z-index: 3;
}

.overlay-element {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(30, 30, 30, 0.55);
    pointer-events: none;
}

/* Rahmen = genau der Bereich, der ausgewertet wird */
.scan-box {
    position: absolute;
    box-sizing: border-box;
    border: 2px solid rgba(255, 255, 255, 0.85);
    border-radius: 6px;
    pointer-events: none;
    z-index: 2;
}

.laser {
    position: absolute;
    left: 3%;
    width: 94%;
    background-color: tomato;
    height: 1px;
    top: 5%;
    box-shadow: 0 0 4px red;
    animation: scanning 2s infinite ease-in-out alternate;
}
@keyframes scanning {
    to { top: 95%; }
}
</style>
